import React from 'react';
import { Box, Card } from '@mui/joy';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';

export interface KanbanCardShellProps {
  /** Raw card id — must match the `SortableContext` items exactly. */
  id: UniqueIdentifier;
  boardScope: string;
  columnId: number;
  /** Rendered inside the drag overlay: no sortable wiring, no transform. */
  isOverlay?: boolean;
  accentColor?: string;
  actions?: ReactNode;
  onClick?: () => void;
  children: ReactNode;
}

const cardSx = (accentColor?: string) =>
  ({
    p: 1.5,
    borderRadius: 'md',
    boxShadow: 'xs',
    cursor: 'pointer',
    position: 'relative',
    ...(accentColor ? { borderLeft: '3px solid', borderLeftColor: accentColor } : {}),
    '&:hover': { boxShadow: 'md' },
    '&:hover .kanban-card-actions': { opacity: 1 },
  }) as const;

const Shell: React.FC<Omit<KanbanCardShellProps, 'id' | 'boardScope' | 'columnId'>> = ({
  accentColor,
  actions,
  onClick,
  children,
}) => (
  <Card variant="outlined" sx={cardSx(accentColor)} onClick={onClick}>
    {actions && (
      <Box
        className="kanban-card-actions"
        // Revealed on hover so a dense board stays readable, but kept in the
        // DOM so keyboard users can still reach the actions.
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          opacity: 0,
          transition: 'opacity 120ms',
          '&:focus-within': { opacity: 1 },
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {actions}
      </Box>
    )}
    {children}
  </Card>
);

const SortableCardShell: React.FC<KanbanCardShellProps> = ({
  id,
  boardScope,
  columnId,
  accentColor,
  actions,
  onClick,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: 'card', boardScope, columnId, cardId: id },
  });

  return (
    <Box
      ref={setNodeRef}
      style={{
        flexShrink: 0,
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      {...attributes}
      {...listeners}
    >
      <Shell accentColor={accentColor} actions={actions} onClick={onClick}>
        {children}
      </Shell>
    </Box>
  );
};

/**
 * The draggable frame around a card body.
 *
 * The column owns this, not the caller: a `renderCard` that returned its own
 * frame would have no sortable wiring, producing a board that renders perfectly
 * and drags nothing. Callers supply only the body.
 *
 * Split into two components so the sortable hook is called unconditionally in
 * the one that needs it — the overlay copy must not register a second sortable
 * under the same id.
 */
export const KanbanCardShell: React.FC<KanbanCardShellProps> = (props) =>
  props.isOverlay ? (
    <Shell accentColor={props.accentColor} actions={props.actions}>
      {props.children}
    </Shell>
  ) : (
    <SortableCardShell {...props} />
  );

export default KanbanCardShell;
